import {
    CmsContentVersionView,
    CmsContentWorkflowStatus,
    CmsHistoryView,
    CmsMicrositeView,
    CmsRenderArtifactStatus,
} from '@ndvr/cms-api';

export interface CmsVersionRow {
    version: CmsContentVersionView;
    status?: CmsContentWorkflowStatus;
    displayStatus: string;
    isPublished: boolean;
    latestActivityAt: string;
    renderHealth?: CmsRenderArtifactStatus;
    renderArtifactGUID?: string;
}

export const isPublishedVersion = (
    version: CmsContentVersionView,
    microsite?: Pick<CmsMicrositeView, 'publishedContentVersionGUID'>,
): boolean => version.cmsContentVersionGUID === microsite?.publishedContentVersionGUID;

export const getVersionDisplayStatus = (
    version: CmsContentVersionView,
    microsite?: Pick<CmsMicrositeView, 'publishedContentVersionGUID'>,
): string => {
    if (isPublishedVersion(version, microsite)) {
        return 'Live';
    }

    if (version.latestWorkflowStatus === CmsContentWorkflowStatus.READY_FOR_REVIEW) {
        return 'Ready for review';
    }

    if (version.latestWorkflowStatus === CmsContentWorkflowStatus.APPROVED) {
        return 'Approved pending publish';
    }

    if (version.latestWorkflowStatus === CmsContentWorkflowStatus.PUBLISHED) {
        return 'Replaced';
    }

    if (version.latestWorkflowStatus === CmsContentWorkflowStatus.CHANGES_REQUESTED) {
        return 'Changes requested';
    }

    return 'Draft';
};

export const deriveCmsVersionRows = (
    history?: CmsHistoryView,
    microsite?: Pick<CmsMicrositeView, 'publishedContentVersionGUID'>,
): CmsVersionRow[] =>
    [...(history?.versions ?? [])]
        .sort((left, right) => right.versionNumber - left.versionNumber)
        .map((version) => {
            const events = history?.workflowEvents.filter(
                (event) => event.cmsContentVersionGUID === version.cmsContentVersionGUID,
            );
            const artifacts = history?.renderArtifacts.filter(
                (artifact) => artifact.cmsContentVersionGUID === version.cmsContentVersionGUID,
            );
            const latestArtifact = artifacts?.[0];
            const failedArtifact =
                latestArtifact?.status === CmsRenderArtifactStatus.FAILED ? latestArtifact : undefined;
            const latestEvent = events?.at(-1);
            const displayStatus =
                failedArtifact && version.latestWorkflowStatus === CmsContentWorkflowStatus.APPROVED
                    ? 'Publish failed'
                    : getVersionDisplayStatus(version, microsite);

            return {
                version,
                status: version.latestWorkflowStatus,
                displayStatus,
                isPublished: isPublishedVersion(version, microsite),
                latestActivityAt: latestEvent?.createdAt ?? latestArtifact?.updatedAt ?? version.submittedAt,
                renderHealth: failedArtifact?.status ?? latestArtifact?.status,
                renderArtifactGUID: latestArtifact?.cmsRenderArtifactGUID,
            };
        });

export const getActiveCmsCandidate = (rows: CmsVersionRow[]): CmsVersionRow | undefined =>
    rows.find(
        (row) =>
            row.status === CmsContentWorkflowStatus.READY_FOR_REVIEW ||
            (row.status === CmsContentWorkflowStatus.APPROVED && !row.isPublished),
    );

export const getPublishableVersionRows = (rows: CmsVersionRow[]): CmsVersionRow[] =>
    rows.filter((row) => row.status === CmsContentWorkflowStatus.APPROVED && !row.isPublished).slice(0, 3);

export const canPublishVersion = (
    row: CmsVersionRow,
    canPublish: boolean,
    publishableRows?: CmsVersionRow[],
): boolean =>
    canPublish &&
    row.status === CmsContentWorkflowStatus.APPROVED &&
    !row.isPublished &&
    (!publishableRows || publishableRows.includes(row));
